// resolveActiveWellFromLocation — resolve the ACTIVE well from the current URL, however you reached
// it (PO 2026-08-05: "make the active well whatever I'm interacting with — from a pad, a timeline,
// a job, any way, not just the wells list"). One place understands every id a well-scoped route can
// carry, so a single app-level hook (useActiveWellFromRoute) marks the well active on ANY such page.
//
// Id sources, in resolve priority:
//   1. a Well id       — path /wells/:wellId (also /pads/:padId/wells/:wellId) or ?wellId=
//   2. a wellbore id   — path /borefields/:id/bore/:boreId or ?boreId= / ?wellboreId=
//   3. a job id        — path /jobs/:jobId (also /bore/:boreId/job/:jobId) or ?jobId=
// A non-well route (/, /pads, /settings, …) carries none → resolves to nothing → the caller no-ops.
//
// Read-only spine walk — no engineering/economic/statistical math (Charter Abs-2).

import type { Well } from "./types";

export interface RouteActiveWell {
  /** The resolved well, or undefined when the route isn't about a specific well. */
  well: Well | undefined;
  /** A job id the URL explicitly names AND that belongs to `well` — adopted as the active job.
   *  undefined when the route names no (owned) job → the well's remembered job is preserved. */
  jobId: string | undefined;
}

/** First path capture of `/<seg>/<id>` where id is a single path segment (stops at /, ?, #). */
function pathId(pathname: string, seg: string): string | undefined {
  const m = pathname.match(new RegExp(`/${seg}/([^/?#]+)`));
  return m?.[1];
}

function jobOnWell(well: Well, jobId: string): boolean {
  return (well.wellbores ?? []).some((wb) =>
    (wb.jobs ?? []).some((j) => j.id === jobId),
  );
}

export function resolveActiveWellFromLocation(
  pathname: string,
  search: string,
  wells: readonly Well[],
): RouteActiveWell {
  const params = new URLSearchParams(search);
  const wellId = pathId(pathname, "wells") ?? params.get("wellId") ?? undefined;
  const wellboreId =
    pathId(pathname, "bore") ??
    params.get("boreId") ??
    params.get("wellboreId") ??
    undefined;
  // Job path segment is plural on the jobs page (/jobs/:jobId) but singular in the legacy
  // bore deep-link (/borefields/:id/bore/:boreId/job/:jobId) — accept both.
  const jobId =
    pathId(pathname, "jobs") ??
    pathId(pathname, "job") ??
    params.get("jobId") ??
    undefined;

  // Resolve the well: explicit Well id → wellbore's owner → job's owner.
  let well: Well | undefined;
  if (wellId) well = wells.find((w) => w.id === wellId);
  if (!well && wellboreId)
    well = wells.find((w) =>
      (w.wellbores ?? []).some((wb) => wb.id === wellboreId),
    );
  if (!well && jobId) well = wells.find((w) => jobOnWell(w, jobId));

  // Adopt the URL's job only when it actually belongs to the resolved well (a stale/foreign jobId
  // must never override the well's remembered job); else undefined → preserve the remembered job.
  return {
    well,
    jobId: well && jobId && jobOnWell(well, jobId) ? jobId : undefined,
  };
}
